import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Box } from '@mui/material';
import { Delete, Upload } from '@mui/icons-material';
import { useDarkMode } from '../context/DarkModeContext';
import TextInputArea from '../components/TextInputArea';
import DiffViewer from '../components/DiffViewer';
import Footer from '../components/Footer';
import '../styles/TextComparePage.css';

export default function TextComparePage() {
  const [leftText, setLeftText] = useState('');
  const [rightText, setRightText] = useState('');
  const [showComparison, setShowComparison] = useState(false);
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const leftFileInputRef = useRef(null);
  const rightFileInputRef = useRef(null);

  const handleCompare = () => {
    setShowComparison(true);
  };

  const handleClear = () => {
    setLeftText('');
    setRightText('');
    setShowComparison(false);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleFileUpload = (event, setText) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setText(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleUploadClick = (fileInputRef) => {
    fileInputRef.current.click();
  };

  return (
    <div className={`container ${darkMode ? 'dark-theme' : ''}`} style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Dark mode toggle in top right corner */}
      <button
        onClick={toggleDarkMode}
        className="dark-mode-toggle"
        style={{ 
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          color: '#fff',
          fontSize: '1rem',
          padding: '8px 16px',
          borderRadius: '4px',
          backgroundColor: '#1976d2',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '40px'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
      >
        <span className="dark-mode-text">
          {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
        </span>
        <span className="dark-mode-icon">
          {darkMode ? '☀️' : '🌙'}
        </span>
      </button>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <img
          src="/favicon.png"
          alt="Home"
          onClick={() => navigate('/')}
          title="Go to Home"
          style={{ height: '60px', width: '60px', cursor: 'pointer', borderRadius: '8px', flexShrink: 0, transition: 'opacity 0.15s' }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.75'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        />
        <h1 className="title" style={{ color: darkMode ? '#42a5f5' : '#1565c0', fontWeight: 'bold', textAlign: 'center', flexGrow: 1, fontSize: '2.5rem', margin: 0 }}>
          Text Compare
        </h1>
      </Box>
      
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={leftFileInputRef}
        style={{ display: 'none' }}
        accept=".txt,.md,.json,.xml,.html,.css,.js,.py,.java,.cpp,.c,.cs,.php,.rb,.go,.rs,.swift,.kt,.scala,.r,.m,.pl,.sh,.bat,.ps1,.sql,.yaml,.yml,.toml,.ini,.cfg,.conf,.log,.csv,.tsv"
        onChange={(e) => handleFileUpload(e, setLeftText)}
      />
      <input
        type="file"
        ref={rightFileInputRef}
        style={{ display: 'none' }}
        accept=".txt,.md,.json,.xml,.html,.css,.js,.py,.java,.cpp,.c,.cs,.php,.rb,.go,.rs,.swift,.kt,.scala,.r,.m,.pl,.sh,.bat,.ps1,.sql,.yaml,.yml,.toml,.ini,.cfg,.conf,.log,.csv,.tsv"
        onChange={(e) => handleFileUpload(e, setRightText)}
      />

      <div className="input-container">
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <span style={{ color: darkMode ? undefined : '#1976d2', fontWeight: 'bold' }}>Original Text</span>
            <Button
              startIcon={<Upload />}
              onClick={() => handleUploadClick(leftFileInputRef)}
              size="small"
              sx={{ 
                color: '#1976d2', 
                borderColor: '#1976d2', 
                border: '1px solid',
                textTransform: 'none',
                fontSize: '0.8rem',
                '&:hover': { 
                  background: 'rgba(25, 118, 210, 0.08)', 
                  borderColor: '#1565c0', 
                  color: '#1565c0' 
                } 
              }}
            >
              Upload File
            </Button>
          </Box>
          <TextInputArea
            value={leftText}
            onChange={setLeftText}
            placeholder="Place your original text here or upload a file"
            onCopy={() => handleCopy(leftText)}
          />
        </div>
        
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <span style={{ color: darkMode ? undefined : '#1976d2', fontWeight: 'bold' }}>Modified Text</span>
            <Button
              startIcon={<Upload />}
              onClick={() => handleUploadClick(rightFileInputRef)}
              size="small"
              sx={{ 
                color: '#1976d2', 
                borderColor: '#1976d2', 
                border: '1px solid',
                textTransform: 'none',
                fontSize: '0.8rem',
                '&:hover': { 
                  background: 'rgba(25, 118, 210, 0.08)', 
                  borderColor: '#1565c0', 
                  color: '#1565c0' 
                } 
              }}
            >
              Upload File
            </Button>
          </Box>
          <TextInputArea
            value={rightText}
            onChange={setRightText}
            placeholder="Place your modified text here or upload a file"
            onCopy={() => handleCopy(rightText)}
          />
        </div>
      </div>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 0, justifyContent: 'center' }}>
        <Button variant="contained" onClick={handleCompare} sx={{ bgcolor: '#1976d2', color: '#fff', fontSize: '1rem', textTransform: 'none', minWidth: 140, '&:hover': { bgcolor: '#1565c0' } }}>
          Compare
        </Button>
        <Button variant="outlined" onClick={handleClear} startIcon={<Delete />} sx={{ color: '#1976d2', borderColor: '#1976d2', background: 'transparent', textTransform: 'none', minWidth: 140, fontSize: '1rem', '&:hover': { background: 'rgba(25, 118, 210, 0.08)', borderColor: '#1565c0', color: '#1565c0' } }}>
          Clear
        </Button>
      </Box>
      {showComparison && (leftText || rightText) && (
        <DiffViewer left={leftText} right={rightText} />
      )}
      <Footer />
    </div>
  );
} 